import { ReportInterval } from '../types/reports';

/**
 * Intervall-Ableitung, Selektor-Sichtbarkeit und Periodenbereinigung fuer die Meldungenseite
 * (D-08, D-09, D-14, D-15, D-16). Drei reine Funktionen ohne React-Bezug.
 *
 * D-15: Quelle der Intervalle ist ausschliesslich das `interval`-Feld der angezeigten Zeilen
 * (seit 22-10 im DTO). Kein neuer Endpunkt, keine Ableitung aus Rollen oder Berechtigungen, kein
 * Nachschlagen in `ReportConfiguration`, und ausdruecklich kein Erraten aus den Datumswerten --
 * eine Jahresmeldung und eine Q1-Meldung liegen beide auf `2026-01-01`.
 *
 * Der Eingabetyp fuer die Zeilen ist bewusst minimal -- `deriveIntervals` kennt weder
 * `UnifiedReport` noch die Seite.
 */

/** Feste, deterministische Reihenfolge -- Vorbedingung fuer den join(',')-Vergleich im Aufrufer. */
const INTERVAL_ORDER: ReportInterval[] = [
  ReportInterval.MONTHLY,
  ReportInterval.QUARTERLY,
  ReportInterval.YEARLY,
];

/**
 * Dedupliziert die `interval`-Werte der uebergebenen Zeilen und bringt sie in eine deterministische
 * Reihenfolge (`monthly`, `quarterly`, `yearly` statt Auftretensreihenfolge). Das ist Absicht, nicht
 * Zufall: der Aufrufer in Plan 04 vergleicht zwei Ergebnisse ueber `join(',')`, um eine Endlosschleife
 * aus „setState im Effekt" zu vermeiden -- dafuer muss dieselbe inhaltliche Menge immer dieselbe
 * Zeichenkette ergeben.
 *
 * D-16: ist `rows` leer, wird `previous` UNVERAENDERT zurueckgegeben -- sonst verschwaende der
 * Selektor genau dann, wenn man ihn zum Zuruecksetzen braucht (z. B. waehrend eines Nachladens).
 */
export function deriveIntervals(
  rows: ReadonlyArray<{ interval: ReportInterval }>,
  previous: ReadonlyArray<ReportInterval>
): ReportInterval[] {
  if (rows.length === 0) {
    return [...previous];
  }

  const present = new Set(rows.map((row) => row.interval));

  return INTERVAL_ORDER.filter((interval) => present.has(interval));
}

/**
 * Liefert das Intervall, unter dem ein Perioden-Selektor erscheinen darf, oder `null`.
 *
 * - D-09: nur wenn `year` gesetzt ist -- sonst entstuende „Q3 ueber alle Jahre hinweg", ein
 *   Filter, den niemand angefordert hat.
 * - D-14: nur wenn genau EIN Intervall vorliegt -- die Bedingung haengt am Intervall, nicht am
 *   Meldungstyp.
 * - nur wenn dieses Intervall feiner als `yearly` ist -- eine Jahresmeldung kennt keine feinere
 *   Periode.
 */
export function periodSelectorInterval(
  intervals: ReadonlyArray<ReportInterval>,
  year: number | undefined
): ReportInterval | null {
  if (year === undefined) {
    return null;
  }

  if (intervals.length !== 1) {
    return null;
  }

  const [only] = intervals;

  if (only === ReportInterval.YEARLY) {
    return null;
  }

  return only;
}

/**
 * D-08: verschwindet der Selektor durch einen Intervallwechsel (`selectorInterval === null`),
 * faellt die gewaehlte Periode aus Filter UND URL weg -- ein stiller Filter, der spaeter eine
 * unerklaerlich leere Liste erzeugt, ist ausdruecklich unerwuenscht.
 */
export function prunePeriod(
  period: string | undefined,
  selectorInterval: ReportInterval | null
): string | undefined {
  if (selectorInterval === null) {
    return undefined;
  }

  return period;
}
